import { useMemo, useState, useCallback, useEffect, memo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Avatar,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
  ListItemIcon,
  Popover,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { Facebook, Instagram, Twitter, YouTube, LinkedIn } from "@mui/icons-material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsIcon from "@mui/icons-material/Groups";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PlaceIcon from "@mui/icons-material/Place";
import LanguageIcon from "@mui/icons-material/Language";
import VerifiedIcon from "@mui/icons-material/Verified";
import HistoryIcon from "@mui/icons-material/History";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentsIcon from "@mui/icons-material/Payments";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import LinkIcon from "@mui/icons-material/Link";
import { useSnackbar } from "notistack";
import { planReviewUrlFor, useCreateBrandShareLink, useRotateBrandShareLink } from "../../hooks/usePlanReview";
import { ShareLinkMenu, copyText, errorMessage } from "../scripts/scriptUi";
import { BRAND_APPROVAL_META, brandDecisionOf } from "./BrandApprovalChip";
import BrandColumnsDialog from "./BrandColumnsDialog";
import ViewColumnOutlinedIcon from "@mui/icons-material/ViewColumnOutlined";
import { usePlanSummary, useRemoveInfluencer, useRemoveInfluencers, useRefreshInfluencer, useTriggerRefreshAll, useRefreshAllStatus, useBulkSetDeliverable, useSetDeliverableCost, useBulkSetBrandDeliverable, useSetBrandDeliverableCost, useCustomDeliverables, useCreateCustomDeliverable, useDeleteCustomDeliverable, useBulkSetCustomField, useSetCustomFieldValue, useCustomFields, useCreateCustomField, useDeleteCustomField, useSetPlanBudget, useSetListBudget, useUpdateInfluencer, useSetContactNumbers, useSetEmails, useDeleteList, useDownloadList } from "../../hooks/usePlanSummary";
import { useDownloadPlan } from "../../hooks/usePlans";
import { useLastViewedInfluencer } from "../../hooks/useLastViewedInfluencer";
import InfluencerTable, { ALL_COLUMNS, InlineEditCell } from "./InfluencerTable";
import WhatsAppBulkSendModal from "./WhatsAppBulkSendModal";
import EmailBulkSendModal from "./EmailBulkSendModal";
import AddInfluencersDrawer from "./AddInfluencersDrawer";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import CreateNewListDrawer from "./CreateNewListDrawer";
import { formatDate } from "./formatDate";
import { canRefreshInfluencer, getCooldownInfo } from "./refreshEligibility";
import { formatCompact } from "./influencerFormat";

const PLATFORM_ICONS = {
  instagram: Instagram,
  youtube: YouTube,
  twitter: Twitter,
  facebook: Facebook,
  linkedin: LinkedIn,
};

const PAGE_SIZE = 25;
// Matches validateBulkRemoveInfluencers' max in pluginbackendnode/models/plan.js.
const BULK_REMOVE_CHUNK_SIZE = 500;
const styles = {
  mainContainer: {
    p: { xs: 2, sm: 3, md: 4 },
    marginLeft: { xs: 0, sm: '65px' },
    marginTop: { xs: 7, sm: 8, md: 8.2 },
    background: 'linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)',
    minHeight: 'calc(100vh - 64px)',
  },
  
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    mb: 3,
    flexWrap: 'wrap',
    gap: 2,
  },
  
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },
  
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexWrap: 'wrap',
  },
  
  addButton: {
    textTransform: "none",
    borderRadius: 2,
    fontWeight: 700,
    bgcolor: "primary.main",
    whiteSpace: 'nowrap',
    '&:hover': { bgcolor: "primary.dark" },
  },

  downloadButton: {
    textTransform: "none",
    borderRadius: 2,
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: "#FF5200",
    borderColor: "rgba(255,82,0,0.45)",
    '&:hover': { borderColor: "#FF5200", bgcolor: "rgba(255,82,0,0.06)" },
  },

  // List tabs styles
  listTabsContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mb: 2,
    flexWrap: 'wrap',
    gap: 1,
  },
  
  listTabs: {
    display: "flex",
    gap: 1,
    flexWrap: 'wrap',
  },
  
  // Active list tab styles
  activeTab: {
    bgcolor: "primary.main",
    border: "1px solid",
    borderColor: "primary.main",
    color: "white",
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: "primary.dark",
      borderColor: "primary.dark",
    },
    '& .MuiChip-label': {
      color: 'white',
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      color: 'rgba(255,255,255,0.7)',
      '&:hover': { color: 'white' },
    },
  },

  inactiveTab: {
    bgcolor: "transparent",
    border: "1px solid",
    borderColor: "grey.200",
    color: "inherit",
    transition: 'all 0.2s ease',
    '&:hover': {
      bgcolor: "grey.50",
      borderColor: "grey.300",
    },
    '& .MuiChip-label': {
      color: 'inherit',
      padding: '0 8px',
    },
    '& .MuiChip-deleteIcon': {
      color: 'grey.400',
      '&:hover': { color: 'error.main' },
    },
  },

  tabDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    transition: 'background-color 0.2s',
  },
  
  tabDotActive: {
    bgcolor: "white",
  },
  
  tabDotInactive: {
    bgcolor: "grey.400",
  },
  
  tabBadge: {
    borderRadius: 5,
    px: 0.75,
    fontSize: 12,
    fontWeight: 700,
    transition: 'all 0.2s',
  },
  
  tabBadgeActive: {
    bgcolor: "rgba(255,255,255,0.25)",
    color: "white",
  },
  
  tabBadgeInactive: {
    bgcolor: "grey.100",
    color: "inherit",
  },
  
  downloadListButton: {
    color: "#FF5200",
    border: "1px solid rgba(255,82,0,0.35)",
    borderRadius: "10px",
    flexShrink: 0,
    '&:hover': { bgcolor: "rgba(255,82,0,0.08)" },
  },

  newListButton: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: 13.5,
    color: "#FF5200",
    bgcolor: "rgba(255,82,0,0.08)",
    border: "1.5px dashed rgba(255,82,0,0.45)",
    borderRadius: "10px",
    px: 2,
    py: 0.75,
    flexShrink: 0,
    transition: "all 0.2s ease",
    '&:hover': {
      bgcolor: "rgba(255,82,0,0.14)",
      borderColor: "#FF5200",
      borderStyle: "solid",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(255,82,0,0.2)",
    },
  },
  
  // Deliverables styles
  deliverablesContainer: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 2,
    flexWrap: 'wrap',
  },

  // Budget bar styles — shared shape for both the plan-level and list-level bars.
  budgetBar: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    mb: 2,
    flexWrap: 'wrap',
  },

  budgetBarItem: {
    display: "flex",
    alignItems: "center",
    gap: 0.75,
  },
  
  // Toolbar styles
  toolbarContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 1.5,
    mb: 1,
    flexWrap: 'wrap',
  },

  toolbarLeftGroup: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    flexWrap: 'wrap',
  },

  toolbarRightGroup: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    flexWrap: 'wrap',
  },

  // The popover only ever shows filter HEADINGS by default — each is its own
  // Accordion, collapsed until clicked, so picking a filter never means
  // scrolling past every other filter's full option list to find it. Rounded
  // + clipped (not just maxHeight/overflow on itself) so the header and the
  // first/last accordion's corners actually follow the Paper's radius instead
  // of poking out square.
  filtersPopover: {
    width: 320,
    borderRadius: "16px",
    overflow: "hidden",
    border: "1px solid",
    borderColor: "grey.100",
    boxShadow: "0 20px 48px rgba(15,23,42,0.16), 0 4px 14px rgba(15,23,42,0.08)",
  },

  filtersPopoverHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    px: 2,
    py: 1.5,
    bgcolor: "#FAFAFB",
    borderBottom: "1px solid",
    borderColor: "grey.100",
  },

  filtersPopoverTitle: {
    fontWeight: 800,
    fontSize: 14.5,
  },

  filtersPopoverScroll: {
    maxHeight: 440,
    overflowY: "auto",
  },

  filterAccordion: {
    boxShadow: "none",
    bgcolor: "transparent",
    '&:before': { display: "none" },
    '&:not(:last-of-type)': {
      borderBottom: "1px solid",
      borderColor: "grey.100",
    },
  },

  filterAccordionSummary: {
    minHeight: 52,
    px: 2,
    gap: 1.25,
    transition: "background-color 0.15s ease",
    '&:hover': { bgcolor: "rgba(0,0,0,0.025)" },
    '&.Mui-expanded': { bgcolor: "rgba(255,82,0,0.05)" },
    '& .MuiAccordionSummary-content': {
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      margin: 0,
    },
  },

  filterAccordionIconWrap: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: "50%",
    bgcolor: "rgba(255,82,0,0.10)",
    color: "#FF5200",
    flexShrink: 0,
  },

  filterAccordionLabel: {
    fontWeight: 700,
    fontSize: 13.5,
    flexGrow: 1,
  },

  filterAccordionBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 20,
    height: 20,
    px: 0.5,
    borderRadius: 5,
    fontSize: 11.5,
    fontWeight: 700,
    bgcolor: "#FF5200",
    color: "#fff",
  },

  filterAccordionDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 0.25,
    pt: 0,
    px: 2,
    pb: 1.5,
    maxHeight: 260,
    overflowY: 'auto',
    bgcolor: "rgba(0,0,0,0.012)",
  },

  filtersSearchField: {
    mb: 0.5,
    mt: 0.5,
    '& .MuiOutlinedInput-root': {
      fontSize: 13,
      borderRadius: '8px',
      bgcolor: "#fff",
    },
  },

  filterCheckbox: {
    color: "grey.400",
    '&.Mui-checked': { color: "#FF5200" },
  },

  filterOptionRow: {
    m: 0,
    width: "100%",
    borderRadius: 1.5,
    px: 0.75,
    '&:hover': { bgcolor: "rgba(0,0,0,0.04)" },
    '& .MuiFormControlLabel-label': { width: "100%" },
  },

  filterOptionLabelRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 1,
    width: "100%",
  },

  filterOptionCount: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 22,
    height: 20,
    px: 0.75,
    borderRadius: 5,
    fontSize: 11,
    fontWeight: 700,
    color: "text.secondary",
    bgcolor: "grey.100",
    flexShrink: 0,
  },

  filterEmptyText: {
    color: "text.secondary",
    fontStyle: "italic",
    py: 0.5,
  },

  filtersClearAllButton: {
    textTransform: "none",
    fontWeight: 700,
    fontSize: 12.5,
    color: "#FF5200",
    minWidth: "auto",
    px: 1,
  },

  matchCountLabel: {
    fontWeight: 700,
    color: "text.secondary",
    whiteSpace: 'nowrap',
  },

  activeFiltersRow: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    flexWrap: 'wrap',
  },

  activeFilterChip: {
    bgcolor: "rgba(255,82,0,0.08)",
    border: "1px solid rgba(255,82,0,0.25)",
    fontWeight: 600,
  },

  searchField: {
    minWidth: { xs: '100%', sm: 240, md: 300 },
    '& .MuiOutlinedInput-root': {
      background: '#F9FAFB',
      borderRadius: '10px',
      fontSize: 14,
      '& fieldset': { borderColor: '#E5E7EB' },
      '&:hover fieldset': { borderColor: '#D1D5DB' },
      '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
    },
  },

  toggleButton: {
    textTransform: "none",
    px: 2,
    transition: 'all 0.2s',
    '&.Mui-selected': {
      bgcolor: "dark.main",
      color: "#fff",
      '&:hover': { bgcolor: "dark.main" },
    },
  },

  columnsButton: {
    textTransform: "none",
    border: "1px solid",
    borderColor: "grey.300",
    borderRadius: 2,
    color: "text.primary",
    whiteSpace: 'nowrap',
  },

  // Same shape as columnsButton, but tinted orange once at least one filter is
  // active — a closed popover shouldn't hide the fact that the table is
  // already narrowed down to a subset of the list.
  filtersButtonActive: {
    textTransform: "none",
    border: "1px solid",
    borderColor: "rgba(255,82,0,0.45)",
    borderRadius: 2,
    bgcolor: "rgba(255,82,0,0.07)",
    color: "#FF5200",
    fontWeight: 700,
    whiteSpace: 'nowrap',
    '&:hover': { bgcolor: "rgba(255,82,0,0.12)", borderColor: "#FF5200" },
  },

  columnsMenu: {
    maxHeight: 300,
    width: 200,
  },

  // Table styles
  tablePaper: {
    borderRadius: 2,
    overflow: 'hidden',
  },
  
  emptyState: {
    textAlign: "center",
    py: 8,
    color: "text.secondary",
  },
  
  emptyStateTitle: {
    fontWeight: 700,
    color: "text.primary",
    mb: 0.5,
  },
  
  // Pagination styles
  paginationContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    mt: 2,
    flexWrap: 'wrap',
    gap: 1,
  },
  
  paginationControls: {
    display: "flex",
    alignItems: "center",
    gap: 1,
  },
  
  paginationButton: {
    '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
  },
  
  // Loading & Error styles
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100vh",
  },
  
  errorContainer: {
    textAlign: "center",
    py: 10,
  },
  
  errorTitle: {
    fontWeight: 700,
    mb: 0.5,
  },
  
  // Avatar styles
  avatar: {
    width: 40,
    height: 40,
    bgcolor: "grey.100",
    flexShrink: 0,
  },
  
  // Typography styles
  planName: {
    fontWeight: 700,
    lineHeight: 1.2,
  },
};
// "Share with brand" — one no-login link per plan where the brand approves or
// rejects creators (plans/PlanReviewPage.jsx). The link is created on the first
// click, then the same Copy/Open/Replace menu as the Scripts brand link, plus
// choosing which columns the brand sees.
const ShareWithBrandButton = memo(({ planId, token, columns }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [anchorEl, setAnchorEl] = useState(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const createLink = useCreateBrandShareLink(planId);
  const rotateLink = useRotateBrandShareLink(planId);

  const handleClick = async (e) => {
    const anchor = e.currentTarget;
    if (!token) {
      try {
        const { brandShareToken } = await createLink.mutateAsync();
        copyText(planReviewUrlFor(brandShareToken), enqueueSnackbar, "Brand link");
      } catch (err) {
        enqueueSnackbar(errorMessage(err, "Couldn't create the brand link."), { variant: "error" });
        return;
      }
    }
    setAnchorEl(anchor);
  };

  const handleRotate = () =>
    rotateLink
      .mutateAsync()
      .then(() => enqueueSnackbar("Brand link replaced — share the new one", { variant: "success" }))
      .catch((err) => enqueueSnackbar(errorMessage(err), { variant: "error" }));

  return (
    <>
      <Tooltip title="A link for the brand to approve or reject this plan's creators" arrow>
        <Button
          variant="outlined"
          startIcon={createLink.isPending ? <CircularProgress size={16} /> : <LinkIcon fontSize="small" />}
          onClick={handleClick}
          disabled={createLink.isPending}
          sx={styles.downloadButton}
        >
          Share with brand
        </Button>
      </Tooltip>
      <ShareLinkMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        url={planReviewUrlFor(token)}
        label="Brand link"
        onRotate={handleRotate}
        extraItems={
          <MenuItem onClick={() => { setAnchorEl(null); setColumnsOpen(true); }}>
            <ListItemIcon><ViewColumnOutlinedIcon fontSize="small" /></ListItemIcon>Choose columns brand sees
          </MenuItem>
        }
      />
      {/* Keyed on open so it starts from the saved choice every time. */}
      <BrandColumnsDialog key={String(columnsOpen)} open={columnsOpen} onClose={() => setColumnsOpen(false)} planId={planId} columns={columns} />
    </>
  );
});

ShareWithBrandButton.displayName = 'ShareWithBrandButton';

// Memoized Header Actions component
const HeaderActions = memo(({ onAddInfluencers, onDownloadPlan, isDownloading, planId, brandShareToken, brandColumns }) => {
  return (
    <Box sx={styles.headerActions}>
      <ShareWithBrandButton planId={planId} token={brandShareToken} columns={brandColumns} />
      <Button
        variant="outlined"
        startIcon={isDownloading ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon fontSize="small" />}
        onClick={onDownloadPlan}
        disabled={isDownloading}
        sx={styles.downloadButton}
      >
        Download Plan
      </Button>
      <Button
        variant="contained"
        startIcon={<PersonAddAlt1Icon fontSize="small" />}
        onClick={onAddInfluencers}
        sx={styles.addButton}
      >
        Add Influencers
      </Button>
    </Box>
  );
});

HeaderActions.displayName = 'HeaderActions';

// Memoized List Tabs component
const ListTabs = memo(({
  lists,
  currentListId,
  onListChange,
  onNewList,
  onDeleteList,
  onDownloadList,
  isDownloading,
}) => {
  return (
    <Box sx={styles.listTabsContainer}>
      <Box sx={styles.listTabs}>
        {lists.map((list) => {
          const isActive = list.id === currentListId;
          const PlatformIcon = PLATFORM_ICONS[list.platform];
          return (
            <Chip
              key={list.id}
              onClick={() => onListChange(list.id)}
              onDelete={(e) => {
                e.stopPropagation();
                onDeleteList(list);
              }}
              deleteIcon={<CloseIcon fontSize="small" titleAccess="Delete list" />}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      ...styles.tabDot,
                      ...(isActive ? styles.tabDotActive : styles.tabDotInactive),
                    }}
                  />
                  {PlatformIcon && (
                    <PlatformIcon
                      sx={{
                        fontSize: 14,
                        color: isActive ? "rgba(255,255,255,0.85)" : "text.secondary",
                      }}
                    />
                  )}
                  <span style={{
                    color: isActive ? 'white' : 'inherit',
                    fontWeight: isActive ? 600 : 400,
                  }}>
                    {list.name}
                  </span>
                  <Box
                    component="span"
                    sx={{
                      ...styles.tabBadge,
                      ...(isActive ? styles.tabBadgeActive : styles.tabBadgeInactive),
                    }}
                  >
                    {list.influencerCount}
                  </Box>
                </Box>
              }
              sx={isActive ? styles.activeTab : styles.inactiveTab}
            />
          );
        })}
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {currentListId && (
          <IconButton
            size="small"
            onClick={onDownloadList}
            disabled={isDownloading}
            title="Download this list"
            sx={styles.downloadListButton}
          >
            {isDownloading ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon fontSize="small" />}
          </IconButton>
        )}
        <Button
          startIcon={<AddIcon sx={{ fontSize: 18 }} />}
          sx={styles.newListButton}
          onClick={onNewList}
        >
          New List
        </Button>
      </Box>
    </Box>
  );
});

ListTabs.displayName = 'ListTabs';

const formatINR = (value) => `₹${Number(value).toLocaleString("en-IN")}`;

// Shared shape for the plan-level and list-level budget rows: an editable
// target (InlineEditCell, same click-to-edit pattern as the cost columns),
// the computed spend next to it, and — once a target is actually set — how
// much of it is left, in red once spend has passed it. Purely informational,
// never blocks setting a cost or adding an influencer past the budget.
const BudgetSummaryBar = memo(({ label, budget, totalCost, influencerCount, totalLastPlanCost, onSetBudget }) => {
  const hasBudget = budget != null;
  const remaining = hasBudget ? budget - totalCost : null;
  const overBudget = remaining != null && remaining < 0;
  // Same totalCost the "Spent" figure already uses (sum of the Influencer
  // Cost column) — just divided across however many influencers it's spread
  // over, so it stays in sync with Spent without any separate computation.
  const average = influencerCount > 0 ? Math.round(totalCost / influencerCount) : 0;

  return (
    <Box sx={styles.budgetBar}>
      <Box sx={styles.budgetBarItem}>
        <Typography variant="body2" color="text.secondary">{label} Budget:</Typography>
        <Box sx={{ minWidth: 70 }}>
          <InlineEditCell
            value={budget}
            type="number"
            emptyLabel="Set budget"
            inputProps={{ min: 0 }}
            formatDisplay={formatINR}
            typographySx={{ fontWeight: 700 }}
            onCommit={onSetBudget}
          />
        </Box>
      </Box>
      <Box sx={styles.budgetBarItem}>
        <Typography variant="body2" color="text.secondary">Spent:</Typography>
        <Typography sx={{ fontWeight: 700 }}>{formatINR(totalCost)}</Typography>
      </Box>
      <Box sx={styles.budgetBarItem}>
        <Typography variant="body2" color="text.secondary">Avg per Influencer:</Typography>
        <Typography sx={{ fontWeight: 700 }}>{formatINR(average)}</Typography>
      </Box>
      <Box sx={styles.budgetBarItem}>
        <Typography variant="body2" color="text.secondary">Total Last Plan Cost:</Typography>
        <Typography sx={{ fontWeight: 700 }}>{formatINR(totalLastPlanCost ?? 0)}</Typography>
      </Box>
      {hasBudget && (
        <Box sx={styles.budgetBarItem}>
          <Typography variant="body2" color="text.secondary">Remaining:</Typography>
          <Typography sx={{ fontWeight: 700, color: overBudget ? "#DC2626" : "#16A34A" }}>
            {overBudget ? "-" : ""}{formatINR(Math.abs(remaining))}
          </Typography>
        </Box>
      )}
    </Box>
  );
});
BudgetSummaryBar.displayName = "BudgetSummaryBar";

// Sentinel option values for "this influencer has no X set" — distinct from
// any real value so each can sit in the same selected-values array as its peers.
const NO_POC_VALUE = "__no_poc__";
const NO_CATEGORY_VALUE = "__no_category__";
const NO_LOCATION_VALUE = "__no_location__";
const NO_LANGUAGE_VALUE = "__no_language__";

// Standard nano/micro/mid/macro/mega reach segmentation — the same breakdown
// influencer-marketing platforms (HypeAuditor, Later, etc.) use industry-wide,
// so "Micro" or "Macro" already means something to whoever's filtering rather
// than being a bucket invented just for this tool.
const FOLLOWER_TIERS = [
  { value: "nano", label: "Nano (<10K)", min: 0, max: 10_000 },
  { value: "micro", label: "Micro (10K–100K)", min: 10_000, max: 100_000 },
  { value: "mid", label: "Mid-tier (100K–500K)", min: 100_000, max: 500_000 },
  { value: "macro", label: "Macro (500K–1M)", min: 500_000, max: 1_000_000 },
  { value: "mega", label: "Mega (1M+)", min: 1_000_000, max: Infinity },
];

// Engagement rate (stored as a plain percentage number, e.g. 1.16 == "1.16%")
// matters more than follower count for gauging real reach — followers are easy
// to inflate, a sustained engagement rate much less so. Thresholds follow the
// commonly-cited Instagram benchmark bands.
const ENGAGEMENT_TIERS = [
  { value: "low", label: "Low (<1%)", min: 0, max: 1 },
  { value: "average", label: "Average (1–3%)", min: 1, max: 3 },
  { value: "good", label: "Good (3–6%)", min: 3, max: 6 },
  { value: "excellent", label: "Excellent (6%+)", min: 6, max: Infinity },
];

// Shared by any tier-bucketed filter (follower/engagement) — null/undefined
// values (data never resolved yet) fall out of every bucket rather than being
// force-fit into "nano"/"low", matching how this table already treats missing
// numeric data everywhere else (sorts sink it to the bottom, never to zero).
const bucketize = (value, tiers) => {
  if (value == null) return null;
  return tiers.find((t) => value >= t.min && value < t.max)?.value ?? null;
};

// A manual min/max range (see RangeFilterInput below) is encoded as one extra
// "custom:<min>-<max>" entry inside that filter's own filterValues[key] array
// — the same array its preset checkboxes already populate — so it rides the
// existing toggle/matches/chip machinery untouched instead of needing its own
// state shape wired through the filter pipeline. Any FILTER_DEFS entry opts
// in with `customRange: true`; only its own matches()/formatChipLabel() need
// to recognize the prefix (currently Follower Tier and Cost Status).
const CUSTOM_RANGE_PREFIX = "custom:";
const encodeCustomRange = (min, max) => `${CUSTOM_RANGE_PREFIX}${min || 0}-${max || ""}`;
const isCustomRangeValue = (value) => value.startsWith(CUSTOM_RANGE_PREFIX);
const parseCustomRange = (value) => {
  const [min, max] = value.slice(CUSTOM_RANGE_PREFIX.length).split("-");
  return { min: Number(min) || 0, max: max ? Number(max) : Infinity };
};
const formatCustomRangeLabel = (value, unitPrefix = "") => {
  const { min, max } = parseCustomRange(value);
  const fmt = (n) => (n === Infinity ? "∞" : `${unitPrefix}${formatCompact(n)}`);
  return `${fmt(min)}–${fmt(max)}`;
};

// Per-influencer cost across whichever deliverables this list currently
// tracks — same scope Cost Status' "added"/"not added" already used, just
// summed instead of merely checked for presence. null (not 0) when none of
// the active deliverables are priced yet, so an unpriced influencer falls out
// of a custom cost range the same way a null followers/engagement value does.
const sumActiveDeliverableCost = (inf, activeDeliverables) => {
  const priced = (inf.deliverableCosts || []).filter(
    (dc) => activeDeliverables.includes(dc.deliverable) && dc.cost != null
  );
  return priced.length ? priced.reduce((sum, dc) => sum + dc.cost, 0) : null;
};

// Declarative filter registry, same shape/intent as ALL_COLUMNS above — a
// future filter is one more entry here (getOptions + matches) rather than a
// bespoke UI, and the Filters menu/active-filter chips render every entry
// generically. Ordered roughly by how a campaign manager actually shortlists:
// reach and quality first, then niche/fit, then operational bookkeeping last.
const FILTER_DEFS = [
  {
    key: "brandApproval",
    label: "Brand Approval",
    icon: VerifiedIcon,
    getOptions: () => Object.entries(BRAND_APPROVAL_META).map(([value, { label }]) => ({ value, label })),
    matches: (inf, selected) => selected.includes(brandDecisionOf(inf.brandApproval)),
  },
  {
    key: "followerTier",
    label: "Follower Tier",
    icon: GroupsIcon,
    customRange: true,
    getOptions: () => FOLLOWER_TIERS.map(({ value, label }) => ({ value, label })),
    matches: (inf, selected) => {
      if (inf.followers == null) return false;
      const tier = bucketize(inf.followers, FOLLOWER_TIERS);
      return selected.some((v) => {
        if (!isCustomRangeValue(v)) return tier === v;
        const { min, max } = parseCustomRange(v);
        return inf.followers >= min && inf.followers <= max;
      });
    },
    // Presets already have a human label from getOptions; only the custom
    // range needs one synthesized, so this returns undefined (falls back to
    // the preset's own label) for anything else.
    formatChipLabel: (value) => (isCustomRangeValue(value) ? formatCustomRangeLabel(value) : undefined),
  },
  {
    key: "engagementTier",
    label: "Engagement Rate",
    icon: TrendingUpIcon,
    getOptions: () => ENGAGEMENT_TIERS.map(({ value, label }) => ({ value, label })),
    matches: (inf, selected) => selected.includes(bucketize(inf.engagementRate, ENGAGEMENT_TIERS)),
  },
  {
    key: "category",
    label: "Category",
    icon: LocalOfferIcon,
    // Open-ended, freeform values (unlike e.g. followerTier's fixed 5 options) —
    // a plan can accumulate dozens of distinct categories, so the menu gets an
    // in-place search box to narrow the checklist instead of scrolling it.
    searchable: true,
    // category can be a comma-joined multi-tag string (e.g. "Beauty, Lifestyle" —
    // see RAPID_INFLUENCER_FIELD_MAP's categories.join(", ") on the backend), so
    // options/matching split on "," rather than treating the whole string as one
    // tag — otherwise "Beauty" and "Beauty, Lifestyle" would wrongly show as
    // unrelated options instead of both containing "Beauty".
    getOptions: (influencers) => {
      const tags = new Set();
      influencers.forEach((i) => {
        (i.category || "").split(",").map((c) => c.trim()).filter(Boolean).forEach((c) => tags.add(c));
      });
      const options = [...tags].sort((a, b) => a.localeCompare(b)).map((c) => ({ value: c, label: c }));
      if (influencers.some((i) => !i.category)) {
        options.push({ value: NO_CATEGORY_VALUE, label: "No category set" });
      }
      return options;
    },
    matches: (inf, selected) => {
      const tags = (inf.category || "").split(",").map((c) => c.trim()).filter(Boolean);
      if (!tags.length) return selected.includes(NO_CATEGORY_VALUE);
      return tags.some((t) => selected.includes(t));
    },
  },
  {
    key: "location",
    label: "Location",
    icon: PlaceIcon,
    // Unlike category, location is left as one exact-match string (e.g. "Mumbai,
    // Maharashtra, India") rather than split — city/state/country only means
    // something together, splitting would group unrelated cities that happen to
    // share a state name.
    getOptions: (influencers) => {
      const locations = [...new Set(influencers.map((i) => i.location).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      );
      const options = locations.map((l) => ({ value: l, label: l }));
      if (influencers.some((i) => !i.location)) {
        options.push({ value: NO_LOCATION_VALUE, label: "No location set" });
      }
      return options;
    },
    matches: (inf, selected) => selected.includes(inf.location || NO_LOCATION_VALUE),
  },
  {
    key: "language",
    label: "Language",
    icon: LanguageIcon,
    getOptions: (influencers) => {
      const langs = new Set();
      influencers.forEach((i) => (i.language || []).forEach((l) => langs.add(l)));
      const options = [...langs].sort((a, b) => a.localeCompare(b)).map((l) => ({ value: l, label: l }));
      if (influencers.some((i) => !(i.language || []).length)) {
        options.push({ value: NO_LANGUAGE_VALUE, label: "No language set" });
      }
      return options;
    },
    matches: (inf, selected) => {
      const langs = inf.language || [];
      if (!langs.length) return selected.includes(NO_LANGUAGE_VALUE);
      return langs.some((l) => selected.includes(l));
    },
  },
  {
    key: "verified",
    label: "Verified",
    icon: VerifiedIcon,
    getOptions: () => [
      { value: "verified", label: "Verified" },
      { value: "not_verified", label: "Not verified" },
    ],
    matches: (inf, selected) => selected.includes(inf.verified ? "verified" : "not_verified"),
  },
  {
    key: "priorHistory",
    label: "Prior Engagement",
    icon: HistoryIcon,
    getOptions: () => [
      { value: "worked_before", label: "Worked with before" },
      { value: "new", label: "New to us" },
    ],
    // hasHistory backs the existing history icon in the table (see
    // buildHasHistorySet in routes/plans.js) — a real "have we ever paid this
    // creator" signal, not something a generic table filter could derive.
    matches: (inf, selected) => selected.includes(inf.hasHistory ? "worked_before" : "new"),
  },
  {
    key: "poc",
    label: "POC",
    icon: PersonOutlineIcon,
    searchable: true,
    // Options come from whichever POCs actually appear on THIS list's
    // influencers, not a separate POC master list — nothing to keep in sync,
    // and there's never an option with zero matching rows.
    getOptions: (influencers) => {
      const pocs = [...new Set(influencers.map((i) => i.poc).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      );
      const options = pocs.map((p) => ({ value: p, label: p }));
      if (influencers.some((i) => !i.poc)) {
        options.push({ value: NO_POC_VALUE, label: "No POC assigned" });
      }
      return options;
    },
    matches: (inf, selected) => selected.includes(inf.poc || NO_POC_VALUE),
  },
  {
    key: "costStatus",
    label: "Cost Status",
    icon: PaymentsIcon,
    customRange: true,
    rangeUnitPrefix: "₹",
    getOptions: () => [
      { value: "added", label: "Cost added" },
      { value: "not_added", label: "Cost not added" },
    ],
    // "Added"/"not added" and the custom min/max range both key off the same
    // sumActiveDeliverableCost — at least one of the list's currently active
    // deliverable columns priced on this influencer (an "Add cost" cell
    // showing means that deliverable isn't added yet), not their full cost
    // history across every deliverable ever priced for them.
    matches: (inf, selected, activeDeliverables) => {
      const cost = sumActiveDeliverableCost(inf, activeDeliverables);
      return selected.some((v) => {
        if (!isCustomRangeValue(v)) return v === (cost != null ? "added" : "not_added");
        if (cost == null) return false;
        const { min, max } = parseCustomRange(v);
        return cost >= min && cost <= max;
      });
    },
    formatChipLabel: (value) => (isCustomRangeValue(value) ? formatCustomRangeLabel(value, "₹") : undefined),
  },
  {
    key: "hasEmail",
    label: "Email",
    icon: EmailIcon,
    getOptions: () => [
      { value: "has_email", label: "Email added" },
      { value: "no_email", label: "Email not added" },
    ],
    matches: (inf, selected) => selected.includes((inf.emails || []).length ? "has_email" : "no_email"),
  },
  {
    key: "hasContactNumber",
    label: "Contact Number",
    icon: PhoneIcon,
    getOptions: () => [
      { value: "has_contact", label: "Contact added" },
      { value: "no_contact", label: "Contact not added" },
    ],
    matches: (inf, selected) =>
      selected.includes((inf.contactNumbers || []).length ? "has_contact" : "no_contact"),
  },
];

// Manual min/max input, rendered under the 5 preset checkboxes in the Follower
// Tier filter — not a separate filter, just another way to populate that same
// def's filterValues[key] array (see CUSTOM_RANGE_PREFIX above), so it works
// for any customRange-flagged FILTER_DEFS entry (Follower Tier, Cost Status)
// unchanged. Local min/max are draft text until Apply commits them, same
// reasoning InfluencerFilters.jsx (the main discovery page's own min/max
// input) already uses: filtering on every keystroke would re-run the match
// on half-typed numbers.
const RangeFilterInput = memo(({ activeRange, onApply, onClear, unitPrefix }) => {
  const [min, setMin] = useState(activeRange?.min ?? "");
  const [max, setMax] = useState(activeRange?.max ?? "");
  // Keeps the draft inputs in sync with the committed range even when it
  // changes from outside this component — the chip's own "x", or Clear all —
  // not just via this component's own Apply/Clear. Depends on the primitive
  // min/max, not the activeRange object itself: activeRangeByKey rebuilds a
  // fresh {min,max} object on every filterValues change (even one from an
  // unrelated filter), and depending on that reference would re-fire this
  // effect and stomp an in-progress, not-yet-applied keystroke.
  useEffect(() => {
    setMin(activeRange?.min ?? "");
    setMax(activeRange?.max ?? "");
  }, [activeRange?.min, activeRange?.max]);
  const invalid = min !== "" && max !== "" && Number(min) > Number(max);
  const adornment = unitPrefix
    ? { startAdornment: <InputAdornment position="start">{unitPrefix}</InputAdornment> }
    : undefined;

  return (
    <Box sx={{ pt: 0.75, mt: 0.5, borderTop: "1px solid rgba(0,0,0,0.06)" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <TextField
          size="small"
          type="number"
          placeholder="Min"
          value={min}
          onChange={(e) => setMin(e.target.value)}
          inputProps={{ min: 0 }}
          InputProps={adornment}
          sx={{ width: unitPrefix ? 96 : 80, '& .MuiOutlinedInput-root': { fontSize: 12.5, borderRadius: '8px', bgcolor: "#fff" } }}
        />
        <Typography sx={{ color: "text.secondary", fontSize: 12.5 }}>–</Typography>
        <TextField
          size="small"
          type="number"
          placeholder="Max"
          value={max}
          onChange={(e) => setMax(e.target.value)}
          inputProps={{ min: 0 }}
          InputProps={adornment}
          sx={{ width: unitPrefix ? 96 : 80, '& .MuiOutlinedInput-root': { fontSize: 12.5, borderRadius: '8px', bgcolor: "#fff" } }}
        />
        <Button
          size="small"
          onClick={() => onApply(min, max)}
          disabled={invalid || (!min && !max)}
          sx={{ textTransform: "none", fontSize: 12, minWidth: "auto", px: 1 }}
        >
          Apply
        </Button>
        {activeRange && (
          <IconButton size="small" onClick={onClear}>
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
      {invalid && (
        <Typography sx={{ color: "error.main", fontSize: 11, mt: 0.25 }}>Min must be less than max</Typography>
      )}
    </Box>
  );
});
RangeFilterInput.displayName = "RangeFilterInput";

// Memoized Toolbar component
const ToolbarActions = memo(({
  view,
  onViewChange,
  columnsAnchorEl,
  onColumnsOpen,
  onColumnsClose,
  visibleColumns,
  onToggleColumn,
  searchQuery,
  onSearchChange,
  filterOptionsByKey,
  filterValues,
  filterSearchByKey,
  onFilterSearchChange,
  filtersAnchorEl,
  onFiltersOpen,
  onFiltersClose,
  onToggleFilterValue,
  onClearFilter,
  onClearAllFilters,
  activeRangeByKey,
  onApplyCustomRange,
  onClearCustomRange,
  matchCount,
  hasWhatsappCampaign,
  onViewWhatsapp,
  onViewEmail,
}) => {
  // Which filter sections are expanded — local to this component (not lifted
  // to PlanSummary) since it's pure display state nothing else needs, and it
  // deliberately persists across closing/reopening the popover: leaving
  // "Category" expanded from your last visit is the expected accordion
  // behavior, not something that should reset every time. Independent, not
  // exclusive — opening one filter doesn't collapse another, since comparing
  // two filters' options side by side is a reasonable thing to want.
  const [expandedFilterKeys, setExpandedFilterKeys] = useState(() => new Set());
  const onToggleExpandedFilter = (key) => {
    setExpandedFilterKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const activeFilterChips = FILTER_DEFS.flatMap((def) =>
    (filterValues[def.key] || []).map((value) => {
      const option = filterOptionsByKey[def.key]?.find((o) => o.value === value);
      const label = def.formatChipLabel?.(value) ?? option?.label ?? value;
      return { key: def.key, value, label: `${def.label}: ${label}` };
    })
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 1 }}>
      <Box sx={styles.toolbarContainer}>
        <Box sx={styles.toolbarLeftGroup}>
          <TextField
            size="small"
            placeholder="Search by name, username, location, category, POC…"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#9CA3AF' }} />
                </InputAdornment>
              ),
            }}
            sx={styles.searchField}
          />

          <Button
            onClick={onFiltersOpen}
            startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
            endIcon={<KeyboardArrowDownIcon />}
            sx={activeFilterChips.length > 0 ? styles.filtersButtonActive : styles.columnsButton}
          >
            Filters{activeFilterChips.length > 0 ? ` (${activeFilterChips.length})` : ""}
          </Button>
          <Popover
            anchorEl={filtersAnchorEl}
            open={Boolean(filtersAnchorEl)}
            onClose={onFiltersClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            PaperProps={{ sx: styles.filtersPopover }}
          >
            <Box sx={styles.filtersPopoverHeader}>
              <Typography sx={styles.filtersPopoverTitle}>Filters</Typography>
              {activeFilterChips.length > 0 && (
                <Button onClick={onClearAllFilters} size="small" sx={styles.filtersClearAllButton}>
                  Clear all
                </Button>
              )}
            </Box>
            <Box sx={styles.filtersPopoverScroll}>
              {FILTER_DEFS.map((def) => {
                const rawOptions = filterOptionsByKey[def.key] || [];
                const query = (filterSearchByKey[def.key] || "").trim().toLowerCase();
                const visibleOptions = def.searchable && query
                  ? rawOptions.filter((opt) => opt.label.toLowerCase().includes(query))
                  : rawOptions;
                const selectedCount = (filterValues[def.key] || []).length;
                const FilterIcon = def.icon;

                return (
                  <Accordion
                    key={def.key}
                    disableGutters
                    square
                    elevation={0}
                    expanded={expandedFilterKeys.has(def.key)}
                    onChange={() => onToggleExpandedFilter(def.key)}
                    sx={styles.filterAccordion}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />} sx={styles.filterAccordionSummary}>
                      <Box sx={styles.filterAccordionIconWrap}>
                        <FilterIcon sx={{ fontSize: 16 }} />
                      </Box>
                      <Typography sx={styles.filterAccordionLabel}>{def.label}</Typography>
                      {selectedCount > 0 && (
                        <Box component="span" sx={styles.filterAccordionBadge}>
                          {selectedCount}
                        </Box>
                      )}
                    </AccordionSummary>
                    <AccordionDetails sx={styles.filterAccordionDetails}>
                      {def.searchable && (
                        <TextField
                          size="small"
                          fullWidth
                          autoComplete="off"
                          placeholder={`Search ${def.label.toLowerCase()}…`}
                          value={filterSearchByKey[def.key] || ""}
                          onChange={(e) => onFilterSearchChange(def.key, e.target.value)}
                          sx={styles.filtersSearchField}
                        />
                      )}
                      {visibleOptions.length ? (
                        visibleOptions.map((opt) => (
                          <FormControlLabel
                            key={opt.value}
                            sx={styles.filterOptionRow}
                            control={
                              <Checkbox
                                size="small"
                                checked={(filterValues[def.key] || []).includes(opt.value)}
                                onChange={() => onToggleFilterValue(def.key, opt.value)}
                                sx={styles.filterCheckbox}
                              />
                            }
                            label={
                              <Box sx={styles.filterOptionLabelRow}>
                                <Typography variant="body2" noWrap>{opt.label}</Typography>
                                <Box component="span" sx={styles.filterOptionCount}>
                                  {opt.count}
                                </Box>
                              </Box>
                            }
                          />
                        ))
                      ) : (
                        <Typography variant="body2" sx={styles.filterEmptyText}>
                          {rawOptions.length ? "No matches" : "No values yet"}
                        </Typography>
                      )}
                      {def.customRange && (
                        <RangeFilterInput
                          activeRange={activeRangeByKey[def.key]}
                          onApply={(min, max) => onApplyCustomRange(def.key, min, max)}
                          onClear={() => onClearCustomRange(def.key)}
                          unitPrefix={def.rangeUnitPrefix}
                        />
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Popover>

          {/* Shown once ANY WhatsApp send has gone out to this plan — a Campaign created
              and run from the WhatsApp > Campaigns screen, or a plain "Send Message"
              bulk-send from this plan's own selection bar (see hasWhatsappCampaign on the
              plan summary response for the exact rule; both count, since the destination
              screen just reads conversations off the plan's influencer list either way). */}
          {hasWhatsappCampaign && (
            <Button
              onClick={onViewWhatsapp}
              startIcon={<WhatsAppIcon sx={{ fontSize: 18 }} />}
              sx={styles.columnsButton}
            >
              View WhatsApp Msg
            </Button>
          )}

          {/* Unlike View WhatsApp Msg above, not gated behind a "has anything ever been
              sent" flag — email threads are per (plan, influencer) and the page's own
              empty state already reads fine with nothing in it yet, so there's no real
              need for the extra backend aggregation just to hide this button early on. */}
          <Button
            onClick={onViewEmail}
            startIcon={<MailOutlineIcon sx={{ fontSize: 18 }} />}
            sx={styles.columnsButton}
          >
            View Emails
          </Button>
        </Box>

        <Box sx={styles.toolbarRightGroup}>
          <ToggleButtonGroup
            exclusive
            value={view}
            onChange={(_, value) => value && onViewChange(value)}
            size="small"
          >
            <ToggleButton value="list" sx={styles.toggleButton}>
              List
            </ToggleButton>
            <ToggleButton value="summary" sx={styles.toggleButton}>
              Summary
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            onClick={onColumnsOpen}
            endIcon={<KeyboardArrowDownIcon />}
            sx={styles.columnsButton}
          >
            Columns Visible
          </Button>
          <Menu
            anchorEl={columnsAnchorEl}
            open={Boolean(columnsAnchorEl)}
            onClose={onColumnsClose}
            PaperProps={{ sx: styles.columnsMenu }}
          >
            {ALL_COLUMNS.map((col) => (
              <MenuItem
                key={col.key}
                onClick={() => onToggleColumn(col.key)}
                dense
              >
                <Checkbox checked={visibleColumns.includes(col.key)} size="small" />
                <ListItemText primary={col.label} />
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>

      {activeFilterChips.length > 0 && (
        <Box sx={styles.activeFiltersRow}>
          <Typography variant="body2" sx={styles.matchCountLabel}>
            {matchCount} influencer{matchCount === 1 ? "" : "s"} match:
          </Typography>
          {activeFilterChips.map((chip) => (
            <Chip
              key={`${chip.key}:${chip.value}`}
              size="small"
              label={chip.label}
              onDelete={() => onClearFilter(chip.key, chip.value)}
              sx={styles.activeFilterChip}
            />
          ))}
        </Box>
      )}
    </Box>
  );
});

ToolbarActions.displayName = 'ToolbarActions';

// Memoized influencer-table pagination component
const InfluencerPaginationControls = memo(({
  page,
  pageCount,
  rangeStart,
  rangeEnd,
  totalCount,
  onPageChange,
}) => {
  return (
    <Box sx={styles.paginationContainer}>
      <Typography color="text.secondary" variant="body2">
        Showing {rangeStart}-{rangeEnd} Influencers of {totalCount}
      </Typography>
      <Box sx={styles.paginationControls}>
        <IconButton
          size="small"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          sx={styles.paginationButton}
        >
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="body2" color="text.secondary">
          {page} of {pageCount}
        </Typography>
        <IconButton
          size="small"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
          sx={styles.paginationButton}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
});

InfluencerPaginationControls.displayName = 'InfluencerPaginationControls';

const PlanSummary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [lastViewedInfluencerId, markInfluencerViewed] = useLastViewedInfluencer(id);
  const { data, isLoading, isError, refetch } = usePlanSummary(id);
  const { enqueueSnackbar } = useSnackbar();
  const removeInfluencerMutation = useRemoveInfluencer(id);
  const removeInfluencersMutation = useRemoveInfluencers(id);
  const refreshInfluencerMutation = useRefreshInfluencer(id);
  const bulkSetDeliverableMutation = useBulkSetDeliverable(id);
  const setDeliverableCostMutation = useSetDeliverableCost(id);
  const bulkSetBrandDeliverableMutation = useBulkSetBrandDeliverable(id);
  const setBrandDeliverableCostMutation = useSetBrandDeliverableCost(id);
  const { data: customDeliverables } = useCustomDeliverables();
  const createCustomDeliverableMutation = useCreateCustomDeliverable();
  const deleteCustomDeliverableMutation = useDeleteCustomDeliverable();
  const bulkSetCustomFieldMutation = useBulkSetCustomField(id);
  const setCustomFieldValueMutation = useSetCustomFieldValue(id);
  const { data: customFieldOptions } = useCustomFields();
  const createCustomFieldMutation = useCreateCustomField();
  const deleteCustomFieldMutation = useDeleteCustomField();
  const setPlanBudgetMutation = useSetPlanBudget(id);
  const setListBudgetMutation = useSetListBudget(id);
  const updateInfluencerMutation = useUpdateInfluencer(id);
  const setContactNumbersMutation = useSetContactNumbers(id);
  const setEmailsMutation = useSetEmails(id);
  const deleteListMutation = useDeleteList(id);
  const downloadPlanMutation = useDownloadPlan();
  const downloadListMutation = useDownloadList(id);
  const triggerRefreshAllMutation = useTriggerRefreshAll(id);

  // State
  const [activeListId, setActiveListId] = useState(null);
  const [view, setView] = useState("list");
  const [columnsAnchorEl, setColumnsAnchorEl] = useState(null);
  // Starts with every column checked except ones ALL_COLUMNS itself flags
  // defaultHidden (see InfluencerTable.jsx) — still fully togglable from
  // Columns Visible afterwards, this only decides the first-open state.
  const [visibleColumns, setVisibleColumns] = useState(
    ALL_COLUMNS.filter((c) => !c.defaultHidden).map((c) => c.key)
  );
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  // Keyed by FILTER_DEFS[].key -> array of selected option values (see NO_POC_VALUE
  // for the "no POC assigned" sentinel). A key absent/empty means "no filter applied".
  const [filterValues, setFilterValues] = useState({});
  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);
  // Keyed by FILTER_DEFS[].key -> the in-menu search box's current text, for
  // searchable filters only (see FILTER_DEFS' searchable flag). Cleared when
  // the menu closes so reopening it never shows a stale, forgotten query.
  const [filterSearchByKey, setFilterSearchByKey] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [newListDrawerOpen, setNewListDrawerOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [deleteListTarget, setDeleteListTarget] = useState(null);
  // Influencer ids checked via the table's row/select-all checkboxes, for
  // bulk delete. Spans every page of the current search/filter/sort — not
  // just the page currently on screen — so "select all" really means all.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkRemoveConfirmOpen, setBulkRemoveConfirmOpen] = useState(false);
  const [bulkMessageOpen, setBulkMessageOpen] = useState(false);
  const [bulkEmailOpen, setBulkEmailOpen] = useState(false);
  // Tracks which influencer(s) currently have a live refresh in flight. A single
  // useMutation instance only ever reflects its LAST call's variables/isPending —
  // if row A's refresh is still pending when row B's is clicked, the mutation's
  // state flips to B and A's button would incorrectly re-enable. This Set tracks
  // per-row in-flight state independently of the shared mutation object.
  const [refreshingIds, setRefreshingIds] = useState(() => new Set());
  // Memoized data
  const plan = useMemo(() => data?.plan, [data]);
  const lists = useMemo(() => data?.lists ?? [], [data]);
  const deliverables = useMemo(() => data?.deliverables ?? {}, [data]);
  const allInfluencers = useMemo(() => data?.influencers ?? [], [data]);

  const currentListId = useMemo(
    () => activeListId ?? lists[0]?.id ?? null,
    [activeListId, lists]
  );

  const currentList = useMemo(
    () => lists.find((l) => l.id === currentListId) ?? null,
    [lists, currentListId]
  );

  // Always enabled while a list is selected — react-query's refetchInterval
  // (2s while isRefreshing, else off) does the rest, so this single hook both
  // resumes showing live progress if the page was reloaded mid-refresh AND
  // stops polling entirely the moment nothing is running.
  const { data: refreshAllStatus } = useRefreshAllStatus(id, currentListId, {
    enabled: Boolean(currentListId),
  });

  // Fires the "N refreshed, M skipped" snackbar exactly once per completed
  // run — hasSeenRefreshing distinguishes "a run just finished" from "this
  // list's last run finished a while ago and we're just now loading its
  // (already-old) completion state," which would otherwise re-toast every
  // time this page mounts.
  const [hasSeenRefreshing, setHasSeenRefreshing] = useState(false);
  useEffect(() => {
    setHasSeenRefreshing(false);
  }, [currentListId]);
  useEffect(() => {
    if (refreshAllStatus?.isRefreshing) setHasSeenRefreshing(true);
  }, [refreshAllStatus?.isRefreshing]);
  useEffect(() => {
    if (!hasSeenRefreshing || !refreshAllStatus || refreshAllStatus.isRefreshing) return;
    const summary = refreshAllStatus.refreshSummary;
    if (!summary) return;

    setHasSeenRefreshing(false);
    queryClient.invalidateQueries({ queryKey: ["plan-summary", id] });

    const { refreshedCount = 0, skipped = [], failed = [] } = summary;
    const extra = (count) => (count > 1 ? ` (+${count - 1} more)` : "");
    // The global default (App.js's SnackbarProvider, 2000ms) is tuned for
    // short confirmations — these two carry a variable-length reason plus a
    // "(+N more)" suffix and are long enough that the default hides them
    // before there's time to read and react, so they get the same longer
    // duration this app already gives other detail-carrying toasts (see
    // Reports.jsx/ReportGroupDashboard.jsx's own 8000ms overrides).
    if (failed.length > 0) {
      enqueueSnackbar(
        `${refreshedCount} refreshed, ${failed.length} failed — ${failed[0].reason}${extra(failed.length)}`,
        { variant: refreshedCount > 0 ? "warning" : "error", autoHideDuration: 8000 }
      );
    } else if (skipped.length > 0) {
      enqueueSnackbar(
        `${refreshedCount} refreshed, ${skipped.length} skipped — ${skipped[0].reason}${extra(skipped.length)}`,
        { variant: "success", autoHideDuration: 8000 }
      );
    } else {
      enqueueSnackbar(`${refreshedCount} creator${refreshedCount === 1 ? "" : "s"} refreshed.`, { variant: "success" });
    }
  }, [hasSeenRefreshing, refreshAllStatus, id, queryClient, enqueueSnackbar]);

  const handleNewListClose = useCallback(() => {
    setNewListDrawerOpen(false);
  }, []);

  const influencersInList = useMemo(
    () =>
      currentListId
        ? allInfluencers.filter((inf) => inf.listId === currentListId)
        : allInfluencers,
    [allInfluencers, currentListId]
  );

  // Per-deliverable spend + fill-count across the WHOLE list, not the current
  // page/search/filter view — same "always against the full list" reasoning
  // filterOptionsByKey's own counts already use, so this summary never shifts
  // under you while scrolling/searching/filtering. Backs the small total/count
  // line InfluencerTable shows under each active Cost column's header.
  const deliverableCostSummary = useMemo(() => {
    const summarize = (field) => {
      const map = {};
      for (const inf of influencersInList) {
        for (const dc of inf[field] || []) {
          if (dc.cost == null) continue;
          const entry = map[dc.deliverable] || { total: 0, count: 0 };
          entry.total += dc.cost;
          entry.count += 1;
          map[dc.deliverable] = entry;
        }
      }
      return map;
    };
    // Same total+count shape as the per-deliverable maps above, for a single
    // scalar cost field instead of an array of deliverables — any other
    // single-value cost column (e.g. Last Cost) can reuse this as-is.
    const summarizeScalar = (field) => {
      let total = 0;
      let count = 0;
      for (const inf of influencersInList) {
        if (inf[field] == null) continue;
        total += inf[field];
        count += 1;
      }
      return { total, count };
    };
    return {
      deliverableCosts: summarize("deliverableCosts"),
      brandDeliverableCosts: summarize("brandDeliverableCosts"),
      lastPlanCost: summarizeScalar("lastPlanCost"),
    };
  }, [influencersInList]);

  // Client-computed breakdown for the "Refresh All" confirmation dialog — no
  // extra request needed, this list is already loaded in full.
  const refreshAllBreakdown = useMemo(() => {
    let eligible = 0;
    let cooldown = 0;
    let ineligible = 0;
    for (const inf of influencersInList) {
      if (!canRefreshInfluencer(inf)) {
        ineligible += 1;
      } else if (getCooldownInfo(inf)) {
        cooldown += 1;
      } else {
        eligible += 1;
      }
    }
    return { total: influencersInList.length, eligible, cooldown, ineligible };
  }, [influencersInList]);

  // Search matches the same fields a POC would actually recall an influencer
  // by — name, handle, and the free-text context columns — not the numeric ones.
  const searchedInfluencers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return influencersInList;
    return influencersInList.filter((inf) =>
      [inf.name, inf.username, inf.location, inf.category, inf.poc].some((field) =>
        field?.toLowerCase().includes(query)
      )
    );
  }, [influencersInList, searchQuery]);

  // Options are built from every influencer currently in this list, not the
  // search/filter-narrowed view — so the Filters menu never shrinks out an
  // option just because something else already filtered its rows out.
  // Each option's count is how many of this list's influencers match it on
  // its own (reusing the same def.matches() the actual filtering runs on, so
  // the two can never disagree) — always against the full list, not further
  // narrowed by whatever else is currently selected, so picking one filter
  // doesn't make every other filter's counts shift under you mid-decision.
  const filterOptionsByKey = useMemo(() => {
    const activeDeliverables = currentList?.activeDeliverables ?? [];
    const map = {};
    FILTER_DEFS.forEach((def) => {
      map[def.key] = def.getOptions(influencersInList).map((opt) => ({
        ...opt,
        count: influencersInList.filter((inf) => def.matches(inf, [opt.value], activeDeliverables)).length,
      }));
    });
    return map;
  }, [influencersInList, currentList]);

  // Keyed by FILTER_DEFS[].key -> that filter's active custom range (if any),
  // pulled back out of its filterValues[key] array for RangeFilterInput to
  // prefill/show as active — see CUSTOM_RANGE_PREFIX above.
  const activeRangeByKey = useMemo(() => {
    const map = {};
    FILTER_DEFS.forEach((def) => {
      if (!def.customRange) return;
      const raw = (filterValues[def.key] || []).find(isCustomRangeValue);
      if (!raw) {
        map[def.key] = null;
        return;
      }
      const { min, max } = parseCustomRange(raw);
      map[def.key] = { min, max: max === Infinity ? "" : max };
    });
    return map;
  }, [filterValues]);

  const filteredInfluencers = useMemo(() => {
    const activeKeys = FILTER_DEFS.filter((def) => filterValues[def.key]?.length);
    if (!activeKeys.length) return searchedInfluencers;

    const activeDeliverables = currentList?.activeDeliverables ?? [];
    return searchedInfluencers.filter((inf) =>
      activeKeys.every((def) => def.matches(inf, filterValues[def.key], activeDeliverables))
    );
  }, [searchedInfluencers, filterValues, currentList]);

  // Generic string/number comparator driven by whichever column header was
  // clicked (see InfluencerTable's TableSortLabel headers) — nulls always sort
  // last regardless of direction, matching how every other data-grid in this
  // app treats "no data" (e.g. formatCompact's "—"), rather than letting them
  // dominate the top of an ascending sort.
  const sortedInfluencers = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key) return filteredInfluencers;
    const factor = direction === "asc" ? 1 : -1;

    return [...filteredInfluencers].sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "string") return av.localeCompare(bv) * factor;
      return (av - bv) * factor;
    });
  }, [filteredInfluencers, sortConfig]);

  const { totalCount, pageCount, pageInfluencers, rangeStart, rangeEnd } = useMemo(() => {
    const total = sortedInfluencers.length;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const start = (page - 1) * PAGE_SIZE;
    const end = page * PAGE_SIZE;

    return {
      totalCount: total,
      pageCount: pages,
      pageInfluencers: sortedInfluencers.slice(start, end),
      rangeStart: total === 0 ? 0 : start + 1,
      rangeEnd: Math.min(end, total),
    };
  }, [sortedInfluencers, page]);

  // "Select all" targets every influencer matching the current search/filter/
  // sort (sortedInfluencers), not just the current page's 25 rows — otherwise
  // selecting "all" of a 500-row list would take 20 separate page visits.
  //
  // Both derived from how many of THIS view's rows are selected — not from
  // selectedIds.size directly, which could be nonzero purely from a selection
  // made under a different search/filter (e.g. select 2 rows, then narrow the
  // search to rows that were never selected — that must show the checkbox
  // fully unchecked, not indeterminate).
  const selectedInView = sortedInfluencers.filter((inf) => selectedIds.has(inf.id)).length;
  const allFilteredSelected = sortedInfluencers.length > 0 && selectedInView === sortedInfluencers.length;
  const someFilteredSelected = selectedInView > 0 && !allFilteredSelected;

  // Drops any selected id that no longer belongs to this list — covers
  // switching lists (a different list's ids just won't match) and a
  // completed delete (removed rows disappear from influencersInList on the
  // refetch this same mutation triggers) without needing a separate reset
  // for either case.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const validIds = new Set(influencersInList.map((inf) => inf.id));
      let changed = false;
      const next = new Set();
      prev.forEach((sid) => {
        if (validIds.has(sid)) next.add(sid);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [influencersInList]);

  // If removing influencers shrinks the list below the current page (e.g. viewing
  // page 2 of 2 and removing enough rows that everything now fits on page 1), snap
  // back instead of showing an empty page until the user manually clicks Previous.
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const deliverablesLabel = useMemo(() => {
    return Object.entries(deliverables)
      .filter(([, value]) => value)
      .map(([platform, value]) => `${platform[0].toUpperCase()}${platform.slice(1)} - ${value}`)
      .join(", ");
  }, [deliverables]);

  // Callbacks
  const handleListChange = useCallback((listId) => {
    setActiveListId(listId);
    setPage(1);
    // A search scoped to the previous list's rows isn't meaningful context once
    // you've switched lists — but the sort preference (e.g. "by followers desc")
    // is a genuine preference, so that one persists across the switch. Filters
    // reset for the same reason as search: a POC selected in list A may not
    // even be an option in list B, and silently carrying it over would look
    // like list B has no matching influencers rather than "filter doesn't apply here".
    setSearchQuery("");
    setFilterValues({});
  }, []);

  const handleSearchChange = useCallback((query) => {
    setSearchQuery(query);
    setPage(1);
  }, []);

  const handleSortChange = useCallback((key) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  }, []);

  const handleNewList = useCallback(() => {
    setNewListDrawerOpen(true);
  }, []);

  const handleDeleteList = useCallback((list) => {
    setDeleteListTarget(list);
  }, []);

  const confirmDeleteList = useCallback(() => {
    const list = deleteListTarget;
    setDeleteListTarget(null);

    deleteListMutation.mutate(
      { listId: list.id },
      {
        onSuccess: () => {
          enqueueSnackbar(`Deleted "${list.name}"`, { variant: "success" });
          // If the list being viewed was the one just deleted, fall back to
          // whichever list is now first — activeListId otherwise still points
          // at an id that no longer exists.
          setActiveListId((prev) => (prev === list.id ? null : prev));
          setPage(1);
        },
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to delete this list",
            { variant: "error" }
          );
        },
      }
    );
  }, [deleteListTarget, deleteListMutation, enqueueSnackbar]);

  const handleViewWhatsapp = useCallback(() => {
    navigate(`/plans-summary/${id}/whatsapp`);
  }, [navigate, id]);

  const handleViewEmail = useCallback(() => {
    navigate(`/plans-summary/${id}/email`);
  }, [navigate, id]);

  const handleDownloadPlan = useCallback(() => {
    downloadPlanMutation.mutate(
      { planId: id },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to download plan",
            { variant: "error" }
          );
        },
      }
    );
  }, [downloadPlanMutation, id, enqueueSnackbar]);

  const handleDownloadList = useCallback(() => {
    if (!currentListId) return;
    downloadListMutation.mutate(
      { listId: currentListId },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to download list",
            { variant: "error" }
          );
        },
      }
    );
  }, [downloadListMutation, currentListId, enqueueSnackbar]);

  const handleViewChange = useCallback((newView) => {
    setView(newView);
  }, []);

  const handleToggleColumn = useCallback((key) => {
    setVisibleColumns((prev) =>
      prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]
    );
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleDrawerOpen = useCallback(() => setDrawerOpen(true), []);
  const handleDrawerClose = useCallback(() => setDrawerOpen(false), []);

  const handleColumnsOpen = useCallback((e) => {
    setColumnsAnchorEl(e.currentTarget);
  }, []);

  const handleColumnsClose = useCallback(() => {
    setColumnsAnchorEl(null);
  }, []);

  const handleFiltersOpen = useCallback((e) => {
    setFiltersAnchorEl(e.currentTarget);
  }, []);

  const handleFiltersClose = useCallback(() => {
    setFiltersAnchorEl(null);
    setFilterSearchByKey({});
  }, []);

  const handleFilterSearchChange = useCallback((filterKey, query) => {
    setFilterSearchByKey((prev) => ({ ...prev, [filterKey]: query }));
  }, []);

  const handleToggleFilterValue = useCallback((filterKey, value) => {
    setFilterValues((prev) => {
      const current = prev[filterKey] || [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [filterKey]: next };
    });
    setPage(1);
  }, []);

  const handleClearFilter = useCallback((filterKey, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [filterKey]: (prev[filterKey] || []).filter((v) => v !== value),
    }));
    setPage(1);
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setFilterValues({});
    setPage(1);
  }, []);

  // Shared by every customRange-flagged filter (Follower Tier, Cost Status).
  // Replaces (rather than toggles) any existing custom range for that key,
  // since a second Apply with new numbers should overwrite the old bound,
  // not stack another entry alongside it.
  const handleApplyCustomRange = useCallback((key, min, max) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []).filter((v) => !isCustomRangeValue(v)), encodeCustomRange(min, max)],
    }));
    setPage(1);
  }, []);

  const handleClearCustomRange = useCallback((key) => {
    setFilterValues((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((v) => !isCustomRangeValue(v)),
    }));
    setPage(1);
  }, []);

  const handleRefetch = useCallback(() => refetch(), [refetch]);

  const handleRemoveInfluencer = useCallback((influencer) => {
    setRemoveTarget(influencer);
  }, []);

  const handleToggleSelectOne = useCallback((influencerId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(influencerId)) next.delete(influencerId);
      else next.add(influencerId);
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allSelected = sortedInfluencers.length > 0 &&
        sortedInfluencers.every((inf) => prev.has(inf.id));
      return allSelected ? new Set() : new Set(sortedInfluencers.map((inf) => inf.id));
    });
  }, [sortedInfluencers]);

  // Every editable column commits in place (see InlineEditCell in
  // InfluencerTable.jsx) — this is the single handler all of them call.
  const handleUpdateInfluencerField = useCallback((influencerId, field, value) => {
    updateInfluencerMutation.mutate(
      { influencerId, [field]: value },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to save that change",
            { variant: "error" }
          );
        },
      }
    );
  }, [updateInfluencerMutation, enqueueSnackbar]);

  const handleRefreshInfluencer = useCallback((influencer) => {
    // Guard against a double-fire on the same row (rapid double-click) — without
    // this, two concurrent requests for the same influencer could both read the
    // cooldown timestamp before either writes it, defeating the whole point of
    // the cooldown.
    if (refreshingIds.has(influencer.id)) return;

    setRefreshingIds((prev) => new Set(prev).add(influencer.id));
    refreshInfluencerMutation.mutate(
      { influencerId: influencer.id },
      {
        onSuccess: (data) => {
          enqueueSnackbar(`Fetched live details for ${data.name}`, { variant: "success" });
        },
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Couldn't fetch live details",
            { variant: "error" }
          );
        },
        onSettled: () => {
          setRefreshingIds((prev) => {
            const next = new Set(prev);
            next.delete(influencer.id);
            return next;
          });
        },
      }
    );
  }, [refreshingIds, refreshInfluencerMutation, enqueueSnackbar]);

  // Direct click-to-fire from the table header's Refresh All icon (no
  // separate confirm step — the hover tooltip already shows the breakdown
  // before the user ever clicks).
  const handleTriggerRefreshAll = useCallback(() => {
    if (!currentListId || triggerRefreshAllMutation.isPending) return;
    triggerRefreshAllMutation.mutate(
      { listId: currentListId },
      {
        onSuccess: (data) => {
          setHasSeenRefreshing(true);
          enqueueSnackbar(`Refreshing ${data.total} creator${data.total === 1 ? "" : "s"}…`, { variant: "info" });
          queryClient.invalidateQueries({ queryKey: ["refresh-all-status", id, currentListId] });
        },
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Couldn't start the refresh — try again",
            { variant: "error" }
          );
        },
      }
    );
  }, [currentListId, triggerRefreshAllMutation, enqueueSnackbar, queryClient, id]);

  const confirmRemoveInfluencer = useCallback(() => {
    const influencer = removeTarget;
    setRemoveTarget(null);
    removeInfluencerMutation.mutate(influencer.id, {
      onSuccess: () => {
        enqueueSnackbar("Influencer removed", { variant: "success" });
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to remove influencer",
          { variant: "error" }
        );
      },
    });
  }, [removeTarget, removeInfluencerMutation, enqueueSnackbar]);

  const confirmBulkRemoveInfluencers = useCallback(async () => {
    const ids = Array.from(selectedIds);
    setBulkRemoveConfirmOpen(false);
    if (!ids.length) return;

    // The backend caps a single request at 500 ids (matches the "select all"
    // checkbox's own max-selectable count) — chunk so a list bigger than that
    // doesn't just 400 outright. Batches are disjoint, so firing them
    // concurrently is safe and faster than one-at-a-time.
    const chunks = [];
    for (let i = 0; i < ids.length; i += BULK_REMOVE_CHUNK_SIZE) {
      chunks.push(ids.slice(i, i + BULK_REMOVE_CHUNK_SIZE));
    }

    try {
      const results = await Promise.all(chunks.map((chunk) => removeInfluencersMutation.mutateAsync(chunk)));
      const removed = results.reduce((sum, r) => sum + (r?.removed ?? 0), 0);
      setSelectedIds(new Set());
      enqueueSnackbar(`Removed ${removed} influencer${removed === 1 ? "" : "s"}`, { variant: "success" });
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to remove influencers",
        { variant: "error" }
      );
    }
  }, [selectedIds, removeInfluencersMutation, enqueueSnackbar]);

  const handleApplyDeliverables = useCallback((deliverables) => {
    if (!currentListId) return;

    bulkSetDeliverableMutation.mutate(
      { listId: currentListId, deliverables },
      {
        onSuccess: (data) => {
          const count = data.activeDeliverables?.length ?? 0;
          enqueueSnackbar(
            count
              ? `Showing Influencer Cost columns for ${count} deliverable(s)`
              : "Reverted to a single Influencer Cost column",
            { variant: "success" }
          );
        },
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to update deliverable columns for this list",
            { variant: "error" }
          );
        },
      }
    );
  }, [currentListId, bulkSetDeliverableMutation, enqueueSnackbar]);

  const handleApplyBrandDeliverables = useCallback((deliverables) => {
    if (!currentListId) return;

    bulkSetBrandDeliverableMutation.mutate(
      { listId: currentListId, deliverables },
      {
        onSuccess: (data) => {
          const count = data.activeBrandDeliverables?.length ?? 0;
          enqueueSnackbar(
            count
              ? `Showing Brand Cost columns for ${count} deliverable(s)`
              : "Reverted to a single Brand Cost column",
            { variant: "success" }
          );
        },
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to update Brand Cost columns for this list",
            { variant: "error" }
          );
        },
      }
    );
  }, [currentListId, bulkSetBrandDeliverableMutation, enqueueSnackbar]);

  // Returns the created (or already-existing) name via the promise so the
  // dropdown can auto-check it — see DeliverableColumnsMenu's handleCreate.
  const handleCreateCustomDeliverable = useCallback((name) => {
    return createCustomDeliverableMutation.mutateAsync({ name }).catch((error) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to create deliverable",
        { variant: "error" }
      );
      throw error;
    });
  }, [createCustomDeliverableMutation, enqueueSnackbar]);

  // Returns the promise so the menu can clear its own local state (draft/
  // checked options) only once the delete has actually gone through.
  const handleDeleteCustomDeliverable = useCallback((name) => {
    return deleteCustomDeliverableMutation.mutateAsync({ name })
      .then(() => {
        enqueueSnackbar(`Deleted "${name}"`, { variant: "success" });
      })
      .catch((error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to delete deliverable",
          { variant: "error" }
        );
        throw error;
      });
  }, [deleteCustomDeliverableMutation, enqueueSnackbar]);

  const handleSetDeliverableCost = useCallback((influencerId, deliverable, cost) => {
    setDeliverableCostMutation.mutate(
      { influencerId, deliverable, cost },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to save cost",
            { variant: "error" }
          );
        },
      }
    );
  }, [setDeliverableCostMutation, enqueueSnackbar]);

  const handleSetBrandDeliverableCost = useCallback((influencerId, deliverable, cost) => {
    setBrandDeliverableCostMutation.mutate(
      { influencerId, deliverable, cost },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to save cost",
            { variant: "error" }
          );
        },
      }
    );
  }, [setBrandDeliverableCostMutation, enqueueSnackbar]);

  const handleApplyCustomFields = useCallback((fields) => {
    if (!currentListId) return;

    bulkSetCustomFieldMutation.mutate(
      { listId: currentListId, fields },
      {
        onSuccess: (data) => {
          const count = data.activeCustomFields?.length ?? 0;
          enqueueSnackbar(
            count
              ? `Showing ${count} custom column(s)`
              : "Reverted to a single Custom Columns placeholder",
            { variant: "success" }
          );
        },
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to update custom columns for this list",
            { variant: "error" }
          );
        },
      }
    );
  }, [currentListId, bulkSetCustomFieldMutation, enqueueSnackbar]);

  // Returns the created (or already-existing) name via the promise so the
  // dropdown can auto-check it — see DeliverableColumnsMenu's handleCreate.
  const handleCreateCustomField = useCallback((name) => {
    return createCustomFieldMutation.mutateAsync({ name }).catch((error) => {
      enqueueSnackbar(
        error?.response?.data?.message || "Failed to create custom column",
        { variant: "error" }
      );
      throw error;
    });
  }, [createCustomFieldMutation, enqueueSnackbar]);

  // Returns the promise so the menu can clear its own local state (draft/
  // checked options) only once the delete has actually gone through.
  const handleDeleteCustomField = useCallback((name) => {
    return deleteCustomFieldMutation.mutateAsync({ name })
      .then(() => {
        enqueueSnackbar(`Deleted "${name}"`, { variant: "success" });
      })
      .catch((error) => {
        enqueueSnackbar(
          error?.response?.data?.message || "Failed to delete custom column",
          { variant: "error" }
        );
        throw error;
      });
  }, [deleteCustomFieldMutation, enqueueSnackbar]);

  const handleSetCustomFieldValue = useCallback((influencerId, field, value) => {
    setCustomFieldValueMutation.mutate(
      { influencerId, field, value },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to save value",
            { variant: "error" }
          );
        },
      }
    );
  }, [setCustomFieldValueMutation, enqueueSnackbar]);

  const handleSetPlanBudget = useCallback((budget) => {
    setPlanBudgetMutation.mutate(
      { budget },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to save plan budget",
            { variant: "error" }
          );
        },
      }
    );
  }, [setPlanBudgetMutation, enqueueSnackbar]);

  const handleSetListBudget = useCallback((budget) => {
    if (!currentListId) return;
    setListBudgetMutation.mutate(
      { listId: currentListId, budget },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to save list budget",
            { variant: "error" }
          );
        },
      }
    );
  }, [currentListId, setListBudgetMutation, enqueueSnackbar]);

  const handleSetContactNumbers = useCallback((influencerId, contactNumbers) => {
    setContactNumbersMutation.mutate(
      { influencerId, contactNumbers },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to save contact number",
            { variant: "error" }
          );
        },
      }
    );
  }, [setContactNumbersMutation, enqueueSnackbar]);

  const handleSetEmails = useCallback((influencerId, emails) => {
    setEmailsMutation.mutate(
      { influencerId, emails },
      {
        onError: (error) => {
          enqueueSnackbar(
            error?.response?.data?.message || "Failed to save email",
            { variant: "error" }
          );
        },
      }
    );
  }, [setEmailsMutation, enqueueSnackbar]);

  // Loading state
  if (isLoading) {
    return (
      <Box sx={styles.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (isError || !plan) {
    return (
      <Box sx={styles.errorContainer}>
        <Typography sx={styles.errorTitle}>
          Couldn't load this plan
        </Typography>
        <Button
          variant="outlined"
          onClick={handleRefetch}
          sx={{ textTransform: "none", borderRadius: 2, mt: 1 }}
        >
          Try again
        </Button>
      </Box>
    );
  }

  return (
    <>
      <DashboardHeaderSection hideSearch />
      <Box sx={styles.mainContainer}>
        {/* Header */}
        <Box sx={styles.header}>
          <Box sx={styles.headerLeft}>
            <IconButton 
              onClick={() => navigate("/plans")} 
              size="small"
              sx={styles.paginationButton}
            >
              <ArrowBackIcon />
            </IconButton>
            <Avatar 
              variant="rounded" 
              src={plan.brandLogo} 
              sx={styles.avatar}
            >
              {plan.brand?.[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h6" sx={styles.planName}>
                {plan.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {plan.brand}
                {plan.createdOn ? ` \u2022 Created on ${formatDate(plan.createdOn)}` : ""}
              </Typography>
            </Box>
          </Box>

          <HeaderActions
            onAddInfluencers={handleDrawerOpen}
            onDownloadPlan={handleDownloadPlan}
            isDownloading={downloadPlanMutation.isPending}
            planId={id}
            brandShareToken={plan.brandShareToken}
            brandColumns={plan.brandVisibleColumns}
          />
        </Box>

        {/* Plan budget */}
        <BudgetSummaryBar
          label="Plan"
          budget={plan?.budget ?? null}
          totalCost={plan?.totalCost ?? 0}
          influencerCount={allInfluencers.length}
          totalLastPlanCost={plan?.totalLastPlanCost ?? 0}
          onSetBudget={handleSetPlanBudget}
        />

        {/* List tabs */}
        <ListTabs
          lists={lists}
          currentListId={currentListId}
          onListChange={handleListChange}
          onNewList={handleNewList}
          onDeleteList={handleDeleteList}
          onDownloadList={handleDownloadList}
          isDownloading={downloadListMutation.isPending}
        />

        {/* List budget */}
        {currentList && (
          <BudgetSummaryBar
            label="List"
            budget={currentList.budget ?? null}
            totalCost={currentList.totalCost ?? 0}
            influencerCount={currentList.influencerCount ?? 0}
            totalLastPlanCost={currentList.totalLastPlanCost ?? 0}
            onSetBudget={handleSetListBudget}
          />
        )}

        {/* Deliverables */}
        {deliverablesLabel && (
          <Box sx={styles.deliverablesContainer}>
            <Typography color="text.secondary" variant="body2">
              Deliverables per Influencer:
            </Typography>
            <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
              {deliverablesLabel}
            </Typography>
          </Box>
        )}

        {/* Toolbar */}
        <ToolbarActions
          view={view}
          onViewChange={handleViewChange}
          columnsAnchorEl={columnsAnchorEl}
          onColumnsOpen={handleColumnsOpen}
          onColumnsClose={handleColumnsClose}
          visibleColumns={visibleColumns}
          onToggleColumn={handleToggleColumn}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          filterOptionsByKey={filterOptionsByKey}
          filterValues={filterValues}
          filterSearchByKey={filterSearchByKey}
          onFilterSearchChange={handleFilterSearchChange}
          filtersAnchorEl={filtersAnchorEl}
          onFiltersOpen={handleFiltersOpen}
          onFiltersClose={handleFiltersClose}
          onToggleFilterValue={handleToggleFilterValue}
          onClearFilter={handleClearFilter}
          onClearAllFilters={handleClearAllFilters}
          activeRangeByKey={activeRangeByKey}
          onApplyCustomRange={handleApplyCustomRange}
          onClearCustomRange={handleClearCustomRange}
          matchCount={totalCount}
          hasWhatsappCampaign={plan.hasWhatsappCampaign}
          onViewWhatsapp={handleViewWhatsapp}
          onViewEmail={handleViewEmail}
        />

        {/* Bulk selection bar — only ever shown in list view, since selection/
            checkboxes are a table-only concept (Summary view has no rows to select). */}
        {view === "list" && selectedIds.size > 0 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              px: 2,
              py: 1,
              mb: 1,
              borderRadius: "10px",
              bgcolor: "#FFF5F0",
              border: "1px solid #FFD9C2",
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 13.5 }}>
              {selectedIds.size} influencer{selectedIds.size === 1 ? "" : "s"} selected
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={() => setSelectedIds(new Set())} sx={{ textTransform: "none" }}>
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => setBulkMessageOpen(true)}
                sx={{ textTransform: "none", boxShadow: "none", bgcolor: "#25D366", "&:hover": { bgcolor: "#1FAF57" } }}
              >
                Send Message
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => setBulkEmailOpen(true)}
                sx={{ textTransform: "none", boxShadow: "none", bgcolor: "#4285F4", "&:hover": { bgcolor: "#3367D6" } }}
              >
                Send Email
              </Button>
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => setBulkRemoveConfirmOpen(true)}
                sx={{ textTransform: "none", boxShadow: "none" }}
              >
                Delete Selected
              </Button>
            </Box>
          </Box>
        )}

        {/* Table */}
        <Paper variant="outlined" sx={styles.tablePaper}>
          {view === "list" ? (
            influencersInList.length === 0 ? (
              <Box sx={styles.emptyState}>
                <Typography sx={styles.emptyStateTitle}>
                  No influencers in this list yet
                </Typography>
                <Typography sx={{ mb: 2 }}>Add influencers to start building this plan.</Typography>
                <Button
                  variant="contained"
                  onClick={handleDrawerOpen}
                  sx={styles.addButton}
                >
                  Add Influencers
                </Button>
              </Box>
            ) : totalCount === 0 ? (
              <Box sx={styles.emptyState}>
                <Typography sx={styles.emptyStateTitle}>
                  No influencers match your search or filters
                </Typography>
                <Typography sx={{ mb: 2 }}>Try adjusting or clearing them.</Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSearchQuery("");
                    handleClearAllFilters();
                  }}
                  sx={styles.downloadButton}
                >
                  Clear search &amp; filters
                </Button>
              </Box>
            ) : (
              <InfluencerTable
                influencers={pageInfluencers}
                visibleColumns={visibleColumns}
                platform={currentList?.platform}
                onRemove={handleRemoveInfluencer}
                selectedIds={selectedIds}
                onToggleSelectOne={handleToggleSelectOne}
                onToggleSelectAll={handleToggleSelectAll}
                allSelected={allFilteredSelected}
                someSelected={someFilteredSelected}
                onRefresh={handleRefreshInfluencer}
                onUpdateField={handleUpdateInfluencerField}
                activeDeliverables={currentList?.activeDeliverables ?? []}
                onApplyDeliverables={handleApplyDeliverables}
                isApplyingDeliverables={bulkSetDeliverableMutation.isPending}
                activeBrandDeliverables={currentList?.activeBrandDeliverables ?? []}
                onApplyBrandDeliverables={handleApplyBrandDeliverables}
                isApplyingBrandDeliverables={bulkSetBrandDeliverableMutation.isPending}
                onSetBrandDeliverableCost={handleSetBrandDeliverableCost}
                customDeliverables={customDeliverables ?? []}
                onCreateCustomDeliverable={handleCreateCustomDeliverable}
                onDeleteCustomDeliverable={handleDeleteCustomDeliverable}
                onSetDeliverableCost={handleSetDeliverableCost}
                deliverableCostTotals={deliverableCostSummary.deliverableCosts}
                brandDeliverableCostTotals={deliverableCostSummary.brandDeliverableCosts}
                lastPlanCostSummary={deliverableCostSummary.lastPlanCost}
                totalInfluencerCount={influencersInList.length}
                activeCustomFields={currentList?.activeCustomFields ?? []}
                onApplyCustomFields={handleApplyCustomFields}
                isApplyingCustomFields={bulkSetCustomFieldMutation.isPending}
                customFieldOptions={customFieldOptions ?? []}
                onCreateCustomField={handleCreateCustomField}
                onDeleteCustomField={handleDeleteCustomField}
                onSetCustomFieldValue={handleSetCustomFieldValue}
                onSetContactNumbers={handleSetContactNumbers}
                onSetEmails={handleSetEmails}
                refreshingIds={refreshingIds}
                sortConfig={sortConfig}
                onSortChange={handleSortChange}
                refreshAllBreakdown={refreshAllBreakdown}
                isRefreshingAll={Boolean(refreshAllStatus?.isRefreshing)}
                refreshAllProgress={refreshAllStatus?.refreshProgress}
                onTriggerRefreshAll={handleTriggerRefreshAll}
                lastViewedId={lastViewedInfluencerId}
                onProfileLinkClick={markInfluencerViewed}
                key={currentListId}
              />
            )
          ) : (
            <Box sx={{ p: 4, color: "text.secondary" }}>
              Summary view coming soon.
            </Box>
          )}
        </Paper>

        {/* Pagination */}
        {totalCount > 0 && (
          <InfluencerPaginationControls
            page={page}
            pageCount={pageCount}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            totalCount={totalCount}
            onPageChange={handlePageChange}
          />
        )}

        {/* Drawer */}
        <AddInfluencersDrawer
          open={drawerOpen}
          onClose={handleDrawerClose}
          lists={lists}
          defaultListId={currentListId}
          planId={id}
        />
        <CreateNewListDrawer
          open={newListDrawerOpen}
          onClose={handleNewListClose}
          planId={id}
        />

        <Dialog
          open={Boolean(removeTarget)}
          onClose={() => setRemoveTarget(null)}
          PaperProps={{ sx: { borderRadius: "16px" } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Remove influencer?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Remove "{removeTarget?.name}" from this list? This can't be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setRemoveTarget(null)}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmRemoveInfluencer}
              color="error"
              variant="contained"
              sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none" }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <WhatsAppBulkSendModal
          open={bulkMessageOpen}
          onClose={() => setBulkMessageOpen(false)}
          planId={id}
          planName={plan.name}
          selectedIds={selectedIds}
        />

        <EmailBulkSendModal
          open={bulkEmailOpen}
          onClose={() => setBulkEmailOpen(false)}
          planId={id}
          selectedIds={selectedIds}
        />

        <Dialog
          open={bulkRemoveConfirmOpen}
          onClose={() => setBulkRemoveConfirmOpen(false)}
          PaperProps={{ sx: { borderRadius: "16px" } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Remove {selectedIds.size} influencer{selectedIds.size === 1 ? "" : "s"}?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Remove {selectedIds.size} influencer{selectedIds.size === 1 ? "" : "s"} from this list? This can't be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setBulkRemoveConfirmOpen(false)}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmBulkRemoveInfluencers}
              color="error"
              variant="contained"
              sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none" }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={Boolean(deleteListTarget)}
          onClose={() => setDeleteListTarget(null)}
          PaperProps={{ sx: { borderRadius: "16px" } }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>Delete list?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Delete "{deleteListTarget?.name}"
              {deleteListTarget?.influencerCount
                ? ` and the ${deleteListTarget.influencerCount} influencer(s) in it`
                : ""}
              ? This can't be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button
              onClick={() => setDeleteListTarget(null)}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDeleteList}
              color="error"
              variant="contained"
              sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default PlanSummary;